import { useCallback } from 'react';
import { devLog } from '@/utils/logger';

/**
 * AI Briefing Settings Hook
 *
 * Manages saving/loading AI briefing form preferences per team.
 * Stores preferences in localStorage keyed by teamId.
 *
 * Phase 0 (Quick Win): Save user preferences locally to avoid re-entering form data
 *
 * Preferences saved:
 * - tone: User's preferred tone of voice
 * - formality: User's preferred formality level (0-10)
 * - length: User's preferred post length
 * - language: User's preferred language
 * - contentFormat: User's preferred content format
 * - wantImageSuggestion: Whether user wants image suggestions by default
 *
 * NOT saved (per-post fields):
 * - platform: Specific to each post
 * - objective: Specific to each post
 * - audience: Specific to each post
 * - context: Specific to each post
 * - imageDescription: Specific to each post
 */

// ============================================================================
// Types
// ============================================================================

export interface AIBriefingSettings {
  tone: string;
  formality: number;
  length: string;
  language: string;
  contentFormat: string;
  wantImageSuggestion: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY_PREFIX = 'ai_briefing_settings';

/**
 * Default settings (match AIBriefingModal defaults)
 */
const DEFAULT_SETTINGS: AIBriefingSettings = {
  tone: 'friendly_professional',
  formality: 5,
  length: 'medium',
  language: 'English',
  contentFormat: 'text',
  wantImageSuggestion: false,
};

// ============================================================================
// Hook
// ============================================================================

export const useAIBriefingSettings = () => {
  /**
   * Get storage key for a specific team
   */
  const getStorageKey = (teamId: number): string => {
    return `${STORAGE_KEY_PREFIX}_${teamId}`;
  };

  /**
   * Get saved settings for a team
   *
   * @param teamId - Team ID to load settings for
   * @returns Saved settings or defaults if none exist
   */
  const getSavedSettings = useCallback((teamId: number): AIBriefingSettings => {
    try {
      const key = getStorageKey(teamId);
      const savedData = localStorage.getItem(key);

      if (!savedData) {
        devLog.log('[useAIBriefingSettings] No saved settings found for team', teamId);
        return DEFAULT_SETTINGS;
      }

      const parsed = JSON.parse(savedData);
      devLog.log('[useAIBriefingSettings] Loaded settings for team', teamId, ':', parsed);

      // Merge with defaults to handle cases where new fields are added
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
      };
    } catch (error) {
      devLog.error('[useAIBriefingSettings] Error loading settings:', error);
      return DEFAULT_SETTINGS;
    }
  }, []);

  /**
   * Save settings for a team
   *
   * @param teamId - Team ID to save settings for
   * @param settings - Settings to save
   */
  const saveSettings = useCallback((teamId: number, settings: AIBriefingSettings): void => {
    try {
      const key = getStorageKey(teamId);
      const data = JSON.stringify(settings);
      localStorage.setItem(key, data);
      devLog.log('[useAIBriefingSettings] Saved settings for team', teamId, ':', settings);
    } catch (error) {
      devLog.error('[useAIBriefingSettings] Error saving settings:', error);
    }
  }, []);

  /**
   * Clear saved settings for a team
   *
   * @param teamId - Team ID to clear settings for
   */
  const clearSettings = useCallback((teamId: number): void => {
    try {
      const key = getStorageKey(teamId);
      localStorage.removeItem(key);
      devLog.log('[useAIBriefingSettings] Cleared settings for team', teamId);
    } catch (error) {
      devLog.error('[useAIBriefingSettings] Error clearing settings:', error);
    }
  }, []);

  return {
    getSavedSettings,
    saveSettings,
    clearSettings,
    defaultSettings: DEFAULT_SETTINGS,
  };
};
